import { useEffect, useState } from 'react';
import chatBotService from '../../../../services/chat-bot.service';
import { DefaultMessage, Message, msgOf } from 'src/model/chat-bot';
import { useStores } from '@src/components/store';
import { GENERIC_CHAT_ERROR } from '@src/components/shared/chat-bot/chat-bot.constants';
import { mapHistoryToMessages } from '@src/utils/chat-bot-utils';
import { EventType } from '@src/dtos/analytics.dto';
import {v4 as uuidv4} from 'uuid';


export function useChatMessages(customerId: number, serviceId: number, isOpen: boolean) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [isAgentLoading, setIsAgentLoading] = useState(false);
    const [lastUserMessage, setLastUserMessage] = useState('');
    const [displayActionButtons, setDisplayActionButtons] = useState<boolean>(false);
    const [chatSessionId, setChatSessionId] = useState<string>();
    const {fwVersionStore} = useStores();

    const isInitial = messages.length === 0;

    const sendEvent = (eventType: EventType, data?: Record<string, any>) => {
        fwVersionStore.sendGeneralEvent(eventType, {chatSessionID: chatSessionId, ...data});
    };

    useEffect(() => {
        if (isOpen) {
            setDisplayActionButtons(true);
            setChatSessionId(uuidv4());
            if (isInitial) {
                void loadChatHistory();
            }
        } else {
            sendEvent(EventType.ChatClose);
            setChatSessionId(undefined);
        }
    }, [isOpen]);

    useEffect(() => {
        if (chatSessionId) {
            sendEvent(EventType.ChatOpen);
        }
    }, [chatSessionId]);

    const addMessages = (...newMessages: Message[]) => {
        setMessages(prev => [...prev, ...newMessages]);
        setDisplayActionButtons(false);
    };

    const onActionButtonClick = (defaultMessage: DefaultMessage) => {
        addDefaultMessage(defaultMessage);
        setDisplayActionButtons(false);
        sendEvent(EventType.ChatActionButtonClick, {howCanIHelpContent: defaultMessage.title});
    };

    const withLoading = async (op: () => Promise<void>, onError?: (e: any) => void): Promise<void> => {
        setIsAgentLoading(true);
        try { await op(); }
        catch (e) { onError?.(e); }
        finally { setIsAgentLoading(false); }
    };

    const loadChatHistory = async () => {
        await withLoading(async () => {
            const chatHistory = await chatBotService.getChatHistory(customerId, serviceId);
            setMessages(mapHistoryToMessages(chatHistory));
        });
    };

    const addDefaultMessage = (defaultMessage: DefaultMessage) => {
        addMessages(
            msgOf.customer(defaultMessage.title),
            msgOf.system(defaultMessage.sample)
        );
    };

    const sendMessage = async () => {
        const trimmed = lastUserMessage.trim();
        if (isAgentLoading || !trimmed) return;
        addMessages(msgOf.customer(trimmed));
        setLastUserMessage('');
        sendEvent(EventType.ChatSend);

        await withLoading(async () => {
            const start = performance.now();
            const answerResponse = await chatBotService.postQuestion(customerId, serviceId, trimmed);
            const responseTimeMs = Math.round(performance.now() - start);
            sendEvent(EventType.ChatMessageReceived, {responseContent: answerResponse.content, responseTime: responseTimeMs, userContent: answerResponse.question});
            addMessages(msgOf.agent(answerResponse.content));
            if (answerResponse.piiFlag) {
                void reFetchLastMessageFromHistory();
            }
            void refreshVersion(answerResponse.externalId);
        }, () => addMessages(msgOf.error(GENERIC_CHAT_ERROR)));
    };

    const refreshVersion = async (externalId?: string) => {
        if (externalId) {
            await fwVersionStore.loadVersionsList();
            if (externalId !== fwVersionStore.selectedFwVersion?.externalId) {
                const version = await fwVersionStore.getVersionData(externalId);
                fwVersionStore.updateSelectedVersionInStore(version);
            }
        }
    };

    const reFetchLastMessageFromHistory = async () => {
        const answerResponse = await chatBotService.getChatHistory(customerId, serviceId, 1);
        setMessages(prev => [
            ...prev.slice(0, -2),
            ...mapHistoryToMessages(answerResponse)
        ]);
    };

    return {
        messages,
        displayActionButtons,
        isAgentLoading,
        lastUserMessage,
        onActionButtonClick,
        setLastUserMessage,
        sendMessage,
        isInitial
    };
}
